import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type { TypeOrArray } from '../common-interfaces';

export interface ScrollingDataPageProps {
  pageNumber: number;
  height?: number;
  intersectionObserver?: IntersectionObserver;
  resizeObserver?: ResizeObserver;
  children: TypeOrArray<ReactNode>;
  isPaginated?: boolean;
}

/**
 * Used to wrap data items in a page of data; when the page reaches the threshold amount (or one of the amounts in the
 * case of an array) then the threshold event is fired with the intersection entries. This component has been memo'd to
 * stop excessive rendering; even when no changes were present this component would render many times. After the memo
 * the component only renders when there are changes.
 * @param {number} pageNumber - Which page this data belongs to.
 * @param {number | undefined} height - The page height; this is set when no data is rendered in the page.
 * @param {IntersectionObserver} intersectionObserver - The intersection observer to use to observe this page.
 * @param {ResizeObserver} resizeObserver - The resize observer used to determine the size of the page.
 * @param {TypeOrArray<React.ReactNode>} children - The items to wrap the data page around.
 * @return {JSX.Element} - A wrapper element that uses the IntersectionObserver to perform its work.
 * @constructor
 */
const ScrollingDataPage = ({
  pageNumber,
  height,
  intersectionObserver,
  resizeObserver,
  children,
  isPaginated = true,
}: ScrollingDataPageProps) => {
  const observedContainer = useRef<Element | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (observedContainer.current && resizeObserver) {
        resizeObserver.unobserve(observedContainer.current);
      }
    };
  }, [resizeObserver]);

  useEffect(() => {
    return () => {
      if (observedContainer.current && intersectionObserver) {
        intersectionObserver.unobserve(observedContainer.current);
      }
    };
  }, [intersectionObserver]);

  const onContainerElementCreated = (element: Element) => {
    if (observedContainer.current) {
      intersectionObserver?.unobserve(observedContainer.current);
      resizeObserver?.unobserve(observedContainer.current);
    }

    intersectionObserver?.observe(element);
    resizeObserver?.observe(element);

    observedContainer.current = element;
  };

  const pageStyles: CSSProperties = height ? { height: `${height}px` } : {};

  return (
    <div
      ref={(element) => {
        if (element) onContainerElementCreated(element);
      }}
      data-page={pageNumber}
      data-name="page"
      className={isPaginated ? 'min-w-full w-fit' : ''}
      style={pageStyles}
    >
      {children}
    </div>
  );
};

export default ScrollingDataPage;
